/**
 * Indicator Panel Assignment Utilities
 *
 * Handles panel assignment for series-as-source feature.
 * When an indicator uses another indicator as source and doesn't require
 * a new axis, it should render in the same panel as its source.
 *
 * @module indicatorPanelAssignment
 */

import { extractSourceIndicatorId } from "./indicatorDependencyValidator.js";
import { isPhaseEnabled } from "../config/featureFlags.js";

/**
 * Group indicators by their effective panel
 *
 * Rules:
 * 1. Indicators with requireNewAxis: true get their own panel
 * 2. Indicators with requireNewAxis: false normally go to main panel
 * 3. BUT if they have a series source, they go to the source's panel
 * 4. Recursively follows source chains to find root panel
 *
 * @param {Array} indicators - List of indicator configurations
 * @returns {Object} Panel assignments
 *   {
 *     mainPanel: [indicators without requireNewAxis and no series source],
 *     separatePanels: [
 *       { primary: indicator, overlays: [dependent indicators] },
 *       ...
 *     ]
 *   }
 *
 * @example
 * // Simple case:
 * [
 *   { id: "vol", renderKey: "volume", requireNewAxis: true },
 *   { id: "ema", renderKey: "ema(9)", requireNewAxis: false, config: { sourcePath: "volume.volume" } }
 * ]
 * // Output: EMA goes to Volume panel
 *
 * @example
 * // Chain case:
 * [
 *   { id: "vol", renderKey: "volume", requireNewAxis: true },
 *   { id: "ema", renderKey: "ema(10)", requireNewAxis: false, config: { sourcePath: "volume.volume" } },
 *   { id: "wma", renderKey: "wma(10)", requireNewAxis: false, config: { sourcePath: "ema(10).ema" } }
 * ]
 * // Output: Both EMA and WMA go to Volume panel (recursive chain resolution)
 */
export function groupIndicatorsByPanel(indicators) {
  if (!indicators || indicators.length === 0) {
    return {
      mainPanel: [],
      separatePanels: [],
    };
  }

  // Phase 3 feature - only active when CALCULATION enabled
  const calculationEnabled = isPhaseEnabled("SERIES_AS_SOURCE", "CALCULATION");

  // Build mapping of ID → indicator for quick lookup
  // IMPORTANT: Use immutable ID as key (not renderKey) for series-as-source lookups
  const indicatorMap = {};
  indicators.forEach((ind) => {
    if (ind.id) {
      indicatorMap[ind.id] = ind;
    }
    // Also map by renderKey for backward compatibility (legacy references)
    if (ind.renderKey) {
      indicatorMap[ind.renderKey] = ind;
    }
  });

  // Find the effective panel for an indicator (recursively follows source chain)
  const getEffectivePanel = (indicator, visited = new Set()) => {
    // CRITICAL: Indicators with requireNewAxis always get their own panel
    // This prevents scale mismatches (e.g., RSI 0-100 vs Price 142000)
    // Even if RSI uses EMA as source, RSI stays in its own panel
    if (indicator.requireNewAxis) {
      return indicator.renderKey || indicator.id;
    }

    // If Phase 3 not enabled, use default logic
    if (!calculationEnabled) {
      return "main";
    }

    // PHASE 3: Only overlay indicators (requireNewAxis: false) follow source panels
    // This ensures proper visual grouping without scale conflicts
    const sourcePath = indicator.config?.sourcePath;
    const sourceIndicatorId = extractSourceIndicatorId(sourcePath);

    // If it has a series source (not OHLC), follow the source's panel
    if (sourceIndicatorId) {
      // Series source detected - follow source panel logic below
      // (continue to source lookup)
    } else {
      // No series source - main panel
      return "main";
    }

    // Prevent infinite recursion (circular dependencies)
    // Use immutable ID for visited tracking
    const indicatorId = indicator.id || indicator.renderKey;
    if (visited.has(indicatorId)) {
      console.warn(
        "[PanelAssignment] Circular dependency detected:",
        indicatorId,
      );
      return "main";
    }
    visited.add(indicatorId);

    // Find source indicator by ID (sourceIndicatorId is now a UUID)
    const sourceIndicator = indicatorMap[sourceIndicatorId];
    if (!sourceIndicator) {
      // Source not found → main panel (fallback)
      console.warn(
        "[PanelAssignment] Source indicator not found:",
        sourceIndicatorId,
        "for:",
        indicator.renderKey,
      );
      return "main";
    }

    // If source requires new axis, use source's panel
    if (sourceIndicator.requireNewAxis) {
      return sourceIndicator.renderKey || sourceIndicator.id;
    }

    // Recursively follow source chain to find root panel
    // Example: WMA → EMA → Volume (Volume has requireNewAxis: true)
    return getEffectivePanel(sourceIndicator, visited);
  };

  // Group indicators by their effective panel
  const panelGroups = {};
  indicators.forEach((indicator) => {
    const panelKey = getEffectivePanel(indicator);
    if (!panelGroups[panelKey]) {
      panelGroups[panelKey] = [];
    }
    panelGroups[panelKey].push(indicator);
  });

  // Extract main panel
  const mainPanel = panelGroups["main"] || [];

  // Build separate panels
  const separatePanels = [];
  Object.keys(panelGroups).forEach((panelKey) => {
    if (panelKey === "main") return;

    const panelIndicators = panelGroups[panelKey];

    // Find the primary indicator (requireNewAxis: true)
    const primary = panelIndicators.find((ind) => ind.requireNewAxis);

    if (!primary) {
      // No primary found (shouldn't happen) → move to main panel
      mainPanel.push(...panelIndicators);
      return;
    }

    // All others are overlays
    const overlays = panelIndicators.filter((ind) => !ind.requireNewAxis);

    separatePanels.push({
      primary,
      overlays,
    });
  });

  return {
    mainPanel,
    separatePanels,
  };
}

/**
 * Get all indicators for a specific panel (primary + overlays)
 *
 * @param {Object} panelConfig - Panel configuration from groupIndicatorsByPanel
 * @returns {Array} All indicators in this panel
 *
 * @example
 * const panel = { primary: volumeIndicator, overlays: [emaIndicator] };
 * getPanelIndicators(panel); // [volumeIndicator, emaIndicator]
 */
export function getPanelIndicators(panelConfig) {
  if (!panelConfig) return [];

  const indicators = [];

  if (panelConfig.primary) {
    indicators.push(panelConfig.primary);
  }

  if (panelConfig.overlays && Array.isArray(panelConfig.overlays)) {
    indicators.push(...panelConfig.overlays);
  }

  return indicators;
}

/**
 * Find which panel an indicator belongs to
 *
 * @param {Object} indicator - Indicator to find
 * @param {Object} panelAssignments - Result from groupIndicatorsByPanel
 * @returns {string} Panel type: 'main' | 'separate' | 'none'
 *
 * @example
 * const assignments = groupIndicatorsByPanel(indicators);
 * findIndicatorPanel(emaIndicator, assignments); // 'separate' (if on volume panel)
 */
export function findIndicatorPanel(indicator, panelAssignments) {
  if (!indicator || !panelAssignments) return "none";

  const indicatorId = indicator.id || indicator.renderKey;

  // Check main panel
  if (
    panelAssignments.mainPanel.some(
      (ind) => ind.id === indicatorId || ind.renderKey === indicatorId,
    )
  ) {
    return "main";
  }

  // Check separate panels
  for (const panel of panelAssignments.separatePanels) {
    const allPanelIndicators = getPanelIndicators(panel);
    if (
      allPanelIndicators.some(
        (ind) => ind.id === indicatorId || ind.renderKey === indicatorId,
      )
    ) {
      return "separate";
    }
  }

  return "none";
}
